/**
 * Web Face Detector - Browser implementation
 * MediaPipe Face Landmarker integration
 */

import { FaceLandmarker, FilesetResolver, FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import type { IFaceDetector } from '@alphahub/eie-core';
import type { EmotionalState } from '@alphahub/eie-core';

interface WebFaceDetectorConfig {
  videoElement?: HTMLVideoElement;
  videoStream?: MediaStream;
  // optional tuning params (e.g. in low light/phone setups)
  minFaceDetectionConfidence?: number;
  minFacePresenceConfidence?: number;
  minTrackingConfidence?: number;
  minFaceSize?: number;
  maxFaceSize?: number;
  /** If true, applies a brightness+contrast canvas filter before passing frames to MediaPipe.
   *  Helps detection in dim or back-lit environments. Default: true. */
  lowLightEnhancement?: boolean;
  /** Brightness multiplier for low-light enhancement (default 1.35). */
  brightnessBoost?: number;
  /** Contrast multiplier for low-light enhancement (default 1.2). */
  contrastBoost?: number;
  /** If true, converts frames to grayscale before MediaPipe processing.
   *  Simulates a monochrome / IR camera: eliminates colour casts, warm/cool light
   *  shadows, and coloured-background interference. MediaPipe is shape-based so it
   *  does not need colour — grayscale is neutral-to-positive in all lighting conditions.
   *  Default: true. */
  grayscaleMode?: boolean;
  /** If true, keep tracking only the face that started the session calibration. */
  enableSessionFaceLock?: boolean;
  /** Number of faces to detect per frame (performance-sensitive). */
  maxFaces?: number;
  /** Matching threshold for locked-face reacquisition. */
  faceLockDistanceThreshold?: number;
  /** Grace period to hold last state while locked face is briefly missing. */
  faceLockGraceMs?: number;
  /** When true, use IMAGE mode + higher confidence for mobile-web stability. */
  isMobileRuntime?: boolean;
}

/**
 * Geometry-based face descriptor using structural ratios that are invariant to
 * position in the frame and distance from the camera.  All values are normalised
 * against the inter-eye distance so they describe the *shape* of a face, not
 * where it happens to sit in the image.
 */
interface FaceDescriptor {
  /** Vertical distance eye-centre → nose tip / inter-eye distance */
  eyeNoseRatio: number;
  /** Vertical distance nose tip → chin / inter-eye distance */
  noseChinRatio: number;
  /** Mouth-corner-to-corner distance / inter-eye distance */
  mouthWidthRatio: number;
  /** Full face height (forehead → chin) / inter-eye distance */
  faceHeightRatio: number;
  /** Vertical distance eye-centre → mouth centre / inter-eye distance */
  eyeMouthRatio: number;
}

interface QualityMetrics {
  resolution: { width: number; height: number };
  faceDetected: boolean;
  faceSize: number;
  confidence: number;
  quality: 'excellent' | 'good' | 'fair' | 'poor';
  warnings: string[];
}

export class WebFaceDetector implements IFaceDetector {
  private config: WebFaceDetectorConfig;
  private videoElement?: HTMLVideoElement;
  private faceLandmarker?: FaceLandmarker;
  private vision?: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;
  private landmarkerDelegate: 'GPU' | 'CPU' = 'GPU';
  private cpuFallbackActivated = false;
  private isInitialized: boolean = false;
  private lastState?: EmotionalState;
  private history: EmotionalState[] = [];
  private qualityMetrics?: QualityMetrics;
  private missingFaceFrames = 0;
  private hasEverDetectedFace = false;
  private readonly RAW_ONLY_AFTER_MISSES = 45;
  private readonly GPU_RESET_AFTER_MISSES = 15;
  private readonly CPU_FALLBACK_AFTER_MISSES = 180;
  private gpuResetAttempted = false;
  /** Canvas used for brightness/contrast preprocessing in low-light scenarios */
  private preprocessCanvas?: HTMLCanvasElement;
  private preprocessCtx?: CanvasRenderingContext2D | null;
  /** Small canvas used to cheaply sample average frame brightness every N frames */
  private brightnessProbeCanvas?: HTMLCanvasElement;
  private brightnessProbeCtx?: CanvasRenderingContext2D | null;
  /** Canvas used for center-crop digital zoom fallback when face appears too small */
  private zoomCanvas?: HTMLCanvasElement;
  private zoomCtx?: CanvasRenderingContext2D | null;
  /** Smoothed average brightness (0-255) of the current frame */
  private avgBrightness: number = 128;
  private brightnessSampleFrame: number = 0;
  private readonly BRIGHTNESS_SAMPLE_EVERY = 10; // re-sample every 10 frames (~0.33s at 30fps)
  /** MediaPipe VIDEO mode requires strictly increasing timestamps for every graph input packet. */
  private lastVideoTimestampMs = 0;

  /** Prevent concurrent detect() calls from interleaving packets into one graph instance. */
  private detectMutex: Promise<void> = Promise.resolve();
  private faceLockDescriptor: FaceDescriptor | null = null;
  private faceLockLastSeenAt = 0;
  private faceLockConsecutiveRejects = 0;

  constructor(config: WebFaceDetectorConfig) {
    this.config = config;
  }

  async initialize(): Promise<void> {
    try {
      console.log('🤖 Loading MediaPipe Face Landmarker...');

      if (this.config.videoElement) {
        this.videoElement = this.config.videoElement;
      } else if (this.config.videoStream) {
        this.videoElement = document.createElement('video');
        this.videoElement.srcObject = this.config.videoStream;
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          // Wider FOV + auto-exposure hints for hidden/dim setups.
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
            facingMode: 'user',
            // Request continuous auto-exposure and auto-focus where supported
            // Cast to any: these are valid MediaTrackConstraints but not in all TS typedefs
            ...({
              exposureMode: 'continuous',
              focusMode: 'continuous',
              whiteBalanceMode: 'continuous',
            } as any),
          },
        });
        this.videoElement = document.createElement('video');
        this.videoElement.srcObject = stream;
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
      }

      await new Promise<void>((resolve) => {
        if (this.videoElement) {
          if (this.videoElement.readyState >= 2) {
            resolve();
          } else {
            this.videoElement.onloadedmetadata = () => resolve();
          }
        } else {
          resolve();
        }
      });

      const initialDelegate = this.config.isMobileRuntime ? 'CPU' : 'GPU';
      await this.createFaceLandmarker(initialDelegate);

      this.isInitialized = true;
      console.log(`✅ MediaPipe loaded (delegate=${this.landmarkerDelegate})`);
    } catch (error) {
      console.error('❌ Failed to initialize MediaPipe:', error);
      throw error;
    }
  }

  private async createFaceLandmarker(delegate: 'GPU' | 'CPU'): Promise<void> {
    this.vision = this.vision ?? await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    if (this.faceLandmarker) {
      this.faceLandmarker.close();
      this.faceLandmarker = undefined;
    }

    this.faceLandmarker = await FaceLandmarker.createFromOptions(this.vision, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
        delegate,
      },
      runningMode: this.config.isMobileRuntime ? 'IMAGE' : 'VIDEO',
      numFaces: Math.max(1, Math.min(4, this.config.maxFaces ?? 1)),
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: false,
      minFaceDetectionConfidence: this.config.isMobileRuntime ? 0.30 : (this.config.minFaceDetectionConfidence ?? 0.10),
      minFacePresenceConfidence: this.config.isMobileRuntime ? 0.30 : (this.config.minFacePresenceConfidence ?? 0.10),
      minTrackingConfidence: this.config.isMobileRuntime ? 0.30 : (this.config.minTrackingConfidence ?? 0.10),
    });
    this.landmarkerDelegate = delegate;
    this.lastVideoTimestampMs = 0;
  }

  private isVideoSourceValid(video: HTMLVideoElement): boolean {
    return (
      video &&
      Number.isFinite(video.videoWidth) &&
      Number.isFinite(video.videoHeight) &&
      video.videoWidth > 0 &&
      video.videoHeight > 0 &&
      video.readyState >= 2
    );
  }

  private isSourceValid(source: HTMLVideoElement | HTMLCanvasElement): boolean {
    if (!source) return false;
    const width = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const height = source instanceof HTMLVideoElement ? source.videoHeight : source.height;
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
  }

  private isMediaPipeROIError(error: unknown): boolean {
    const msg = String(error ?? '').toLowerCase();
    return msg.includes('roi contains nan') || msg.includes('roi contains nan values') || msg.includes('invalid_argument');
  }

  async detect(): Promise<EmotionalState | null> {
    let releaseLock!: () => void;
    const waitForTurn = this.detectMutex;
    this.detectMutex = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });
    await waitForTurn;

    try {
      if (!this.isInitialized || !this.faceLandmarker || !this.videoElement) return null;
      if (!this.isVideoSourceValid(this.videoElement)) return null;

      // On mobile, skip canvas preprocessing (ctx.filter is unreliable on
      // CPU fallback and sustained missing-face bypass preprocessing to avoid
      // canvas overhead when detection is already struggling.
      const forceRawSource = this.cpuFallbackActivated || this.missingFaceFrames >= this.RAW_ONLY_AFTER_MISSES;
      const source = forceRawSource ? this.videoElement : this.getPreprocessedSource();
      if (!this.isSourceValid(source)) return null;

      let result: FaceLandmarkerResult;
      try {
        if (this.config.isMobileRuntime) {
          // IMAGE mode: pass source directly — detect() handles video elements natively
          result = this.faceLandmarker.detect(source);
        } else {
          result = this.faceLandmarker.detectForVideo(source, this.getNextVideoTimestamp());
        }
      } catch (detectError) {
        if (this.isMediaPipeROIError(detectError)) {
          console.warn('[EIE][FACE] MediaPipe ROI NaN error; skipping frame and resetting detector.');
          if (this.hasEverDetectedFace) this.missingFaceFrames += 1;
          this.lastVideoTimestampMs = 0;
          try {
            await this.createFaceLandmarker(this.landmarkerDelegate);
          } catch (resetError) {
            console.error('[EIE][FACE] Unable to recover from ROI NaN, detector reset failed:', resetError);
          }
          return null;
        }
        throw detectError;
      }


      // If preprocessing hurts detection for a specific frame, retry once on raw video.
      // This keeps low-light enhancement benefits while avoiding persistent "no face" loops.
      // On mobile IMAGE mode, skip retry passes entirely: each detect() allocates/deallocates
      // GPU textures, and 3 calls per tick at 100ms saturates mobile GPU causing sustained
      // face loss. A single detect per tick keeps GPU healthy.
      if (!this.config.isMobileRuntime) {
        const noFace = !result.faceLandmarks || result.faceLandmarks.length === 0;
        if (noFace) {
          const zoomFactor = this.missingFaceFrames >= 120 ? 1.45 : 1.30;
          const zoomedSource = this.getCenterZoomSource(source, zoomFactor);
          if (zoomedSource) {
            const zoomResult = this.faceLandmarker.detectForVideo(zoomedSource, this.getNextVideoTimestamp());
            if (zoomResult.faceLandmarks && zoomResult.faceLandmarks.length > 0) {
              result = zoomResult;
            }
          }
        }

        const usedPreprocessedSource = source !== this.videoElement;
        const noFaceAfterZoom = !result.faceLandmarks || result.faceLandmarks.length === 0;
        if (noFaceAfterZoom && usedPreprocessedSource) {
          const rawResult = this.faceLandmarker.detectForVideo(this.videoElement, this.getNextVideoTimestamp());
          if (rawResult.faceLandmarks && rawResult.faceLandmarks.length > 0) {
            result = rawResult;
          }
        }
      }

      const stillNoFace = !result.faceLandmarks || result.faceLandmarks.length === 0;
      if (stillNoFace) {
        if (this.hasEverDetectedFace) this.missingFaceFrames += 1;

        // Quick GPU reset: the GPU delegate on mobile browsers corrupts after
        // sustained use in IMAGE mode. Recreating the instance recovers it.
        if (
          !this.cpuFallbackActivated &&
          !this.gpuResetAttempted &&
          this.landmarkerDelegate === 'GPU' &&
          this.missingFaceFrames >= this.GPU_RESET_AFTER_MISSES
        ) {
          try {
            console.warn(`[EIE][FACE] GPU reset after ${this.missingFaceFrames} misses — recreating GPU landmarker`);
            await this.createFaceLandmarker('GPU');
            this.gpuResetAttempted = true;
            this.missingFaceFrames = 0;
            return this.lastState ?? null;
          } catch (resetError) {
            console.error('[EIE][FACE] GPU reset failed:', resetError);
          }
        }

        if (
          !this.cpuFallbackActivated &&
          this.landmarkerDelegate === 'GPU' &&
          this.missingFaceFrames >= this.CPU_FALLBACK_AFTER_MISSES
        ) {
          try {
            console.warn('[EIE][FACE] Sustained no-face on GPU after reset, switching FaceLandmarker to CPU fallback');
            await this.createFaceLandmarker('CPU');
            this.cpuFallbackActivated = true;
            this.missingFaceFrames = 0;
            return this.lastState ?? null;
          } catch (fallbackError) {
            console.error('[EIE][FACE] CPU fallback initialization failed:', fallbackError);
          }
        }
      }

      const selectedFaceIndex = this.selectFaceIndex(result);
      const quality = this.assessQuality(result, selectedFaceIndex >= 0 ? selectedFaceIndex : 0);
      this.qualityMetrics = quality;

      if (selectedFaceIndex < 0) {
        const graceMs = this.config.faceLockGraceMs ?? 1200;
        if (this.faceLockDescriptor && (Date.now() - this.faceLockLastSeenAt) <= graceMs && this.lastState) {
          return this.lastState;
        }
        // Locked face is gone past grace period — return an out-of-frame marker
        // so the UI can show "no face" and pause emotion classification.
        if (this.faceLockDescriptor) {
          return {
            emotion: 'neutral',
            valence: 0,
            arousal: 0,
            confidence: 0,
            timestamp: Date.now(),
            cognitive: 'out_of_frame' as any,
            faceLockPaused: true,
          } as any;
        }
        return null;
      }

      let blendshapes: Record<string, number> = {};
      if (
        result.faceBlendshapes
        && result.faceBlendshapes.length > selectedFaceIndex
        && result.faceBlendshapes[selectedFaceIndex]?.categories
      ) {
        blendshapes = this.compressBlendshapes(result.faceBlendshapes[selectedFaceIndex].categories);
      }

      // Expose ambient lighting as numeric meta-signals for downstream robustness.
      // Values are added as synthetic blendshape keys (prefixed with __) so existing
      // emotion AU logic remains unchanged.
      const ambientLuma = Math.max(0, Math.min(255, this.avgBrightness));
      const ambientLightNorm = Math.max(0, Math.min(1, ambientLuma / 255));
      const lowLightSeverity = Math.max(0, Math.min(1, (0.45 - ambientLightNorm) / 0.30));
      blendshapes.__ambientLuma = Math.round(ambientLuma * 1000) / 1000;
      blendshapes.__ambientLightNorm = Math.round(ambientLightNorm * 1000) / 1000;
      blendshapes.__lowLightSeverity = Math.round(lowLightSeverity * 1000) / 1000;

      // Keep a short fallback when face momentarily disappears: use last landmarks/state
      if ((!result.faceLandmarks || result.faceLandmarks.length === 0) && this.lastState) {
        if (this.missingFaceFrames <= 3) {
          return this.lastState;
        }
      } else {
        this.missingFaceFrames = 0;
        this.hasEverDetectedFace = true;
        this.gpuResetAttempted = false;
      }

      const state: EmotionalState = {
        emotion: 'neutral',
        valence: 0,
        arousal: 0,
        confidence: quality.confidence,
        timestamp: Date.now(),
        blendshapes,
        faceLandmarks: result.faceLandmarks?.[selectedFaceIndex]?.map((lm, i) => {
          const raw: any = lm as any;
          // Try multiple possible fields from different MP/task versions: visibility, presence, inView, score
          let rawVis: any = raw.visibility ?? raw.presence ?? raw.inView ?? raw.score;
          let vis: number | undefined;
          if (typeof rawVis === 'number' && Number.isFinite(rawVis)) vis = Math.max(0, Math.min(1, rawVis));
          else if (typeof rawVis === 'boolean') vis = rawVis ? 1 : 0;
          else if (typeof raw.visibility === 'boolean') vis = raw.visibility ? 1 : 0;
          else if (typeof raw.presence === 'boolean') vis = raw.presence ? 1 : 0;
          const visibility = typeof vis === 'number' ? vis : 1;
          return { i, x: raw.x, y: raw.y, z: raw.z, visibility };
        }),
      };

      (state as any).multiFaceCount = result.faceLandmarks?.length ?? 0;
      (state as any).faceLockActive = !!this.faceLockDescriptor;
      (state as any).selectedFaceIndex = selectedFaceIndex;
      (state as any).lighting = {
        ambientLuma: blendshapes.__ambientLuma,
        ambientLightNorm: blendshapes.__ambientLightNorm,
        lowLightSeverity: blendshapes.__lowLightSeverity,
      };

      this.lastState = state;
      this.history.push(state);
      if (this.history.length > 100) this.history.shift();

      return state;
    } catch (error) {
      const message = String(error ?? '');
      if (message.includes('Packet timestamp mismatch')) {
        console.warn('[EIE][FACE] Timestamp mismatch detected; resetting FaceLandmarker graph');
        this.lastVideoTimestampMs = 0;
        try {
          await this.createFaceLandmarker(this.landmarkerDelegate);
        } catch (resetError) {
          console.error('[EIE][FACE] Failed to reset FaceLandmarker after timestamp mismatch:', resetError);
        }
      }
      console.error('❌ Detection error:', error);
      return null;
    } finally {
      releaseLock();
    }
  }

  getVideoElement(): HTMLVideoElement | undefined {
    return this.videoElement;
  }

  getLastState(): EmotionalState | undefined {
    return this.lastState;
  }

  getQualityMetrics(): QualityMetrics | undefined {
    return this.qualityMetrics;
  }

  lockCurrentFaceFromLandmarks(landmarks?: Array<{ i?: number; x: number; y: number; z?: number }>): boolean {
    if (!landmarks || landmarks.length === 0) return false;
    const descriptor = this.computeDescriptorFromIndexedLandmarks(landmarks);
    if (!descriptor) return false;
    this.faceLockDescriptor = descriptor;
    this.faceLockLastSeenAt = Date.now();
    console.log(
      `[EIE][FACE-LOCK] locked descriptor: eyeNose=${descriptor.eyeNoseRatio.toFixed(3)} noseChin=${descriptor.noseChinRatio.toFixed(3)} mouthW=${descriptor.mouthWidthRatio.toFixed(3)} faceH=${descriptor.faceHeightRatio.toFixed(3)} eyeMouth=${descriptor.eyeMouthRatio.toFixed(3)} threshold=${(this.config.faceLockDistanceThreshold ?? 0.55).toFixed(3)}`,
    );
    return true;
  }

  clearFaceLock(): void {
    this.faceLockDescriptor = null;
    this.faceLockLastSeenAt = 0;
  }

  getFaceLockStatus(): { locked: boolean; lastSeenAt: number } {
    return { locked: !!this.faceLockDescriptor, lastSeenAt: this.faceLockLastSeenAt };
  }

  async dispose(): Promise<void> {
    console.log('🛑 Disposing WebFaceDetector...');
    // Only release stream if we created the video element (not passed from outside)
    if (!this.config.videoElement && this.videoElement?.srcObject) {
      const stream = this.videoElement.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      this.videoElement.srcObject = null;
    }
    if (this.faceLandmarker) {
      this.faceLandmarker.close();
    }
    this.vision = undefined;
    this.cpuFallbackActivated = false;
    this.landmarkerDelegate = this.config.isMobileRuntime ? 'CPU' : 'GPU';
    this.missingFaceFrames = 0;
    this.hasEverDetectedFace = false;
    this.gpuResetAttempted = false;
    this.preprocessCanvas = undefined;
    this.preprocessCtx = undefined;
    this.brightnessProbeCanvas = undefined;
    this.brightnessProbeCtx = undefined;
    this.zoomCanvas = undefined;
    this.zoomCtx = undefined;

    this.lastVideoTimestampMs = 0;
    this.faceLockDescriptor = null;
    this.faceLockLastSeenAt = 0;
    this.isInitialized = false;
  }


  private getNextVideoTimestamp(preferred?: number): number {
    const candidate = preferred ?? Date.now();
    const next = Math.max(candidate, this.lastVideoTimestampMs + 1);
    this.lastVideoTimestampMs = next;
    return next;
  }

  private getCenterZoomSource(source: HTMLVideoElement | HTMLCanvasElement, zoomFactor: number): HTMLCanvasElement | null {
    const w = this.videoElement?.videoWidth ?? 0;
    const h = this.videoElement?.videoHeight ?? 0;
    if (!w || !h || zoomFactor <= 1) return null;

    try {
      if (!this.zoomCanvas) {
        this.zoomCanvas = document.createElement('canvas');
        this.zoomCtx = this.zoomCanvas.getContext('2d');
      }
      if (!this.zoomCtx) return null;
      if (this.zoomCanvas.width !== w || this.zoomCanvas.height !== h) {
        this.zoomCanvas.width = w;
        this.zoomCanvas.height = h;
      }

      const srcW = w / zoomFactor;
      const srcH = h / zoomFactor;
      const sx = (w - srcW) / 2;
      const sy = (h - srcH) / 2;

      this.zoomCtx.clearRect(0, 0, w, h);
      this.zoomCtx.drawImage(source, sx, sy, srcW, srcH, 0, 0, w, h);
      return this.zoomCanvas;
    } catch {
      return null;
    }
  }

  /**
   * Returns an adaptively brightness/contrast-enhanced canvas frame from the current video.
   * Samples the mean luminance of the frame every N frames and only applies a boost when
   * the scene is actually dark. In well-lit conditions the filter is effectively a no-op
   * (brightness=1, contrast=1), so MediaPipe receives a normal image with no degradation.
   */
  private getPreprocessedSource(): HTMLVideoElement | HTMLCanvasElement {
    const video = this.videoElement!;
    const enhance = this.config.lowLightEnhancement !== false; // default ON
    const grayscale = this.config.grayscaleMode === true;      // default OFF (only apply if explicitly enabled)

    // If neither feature is needed, pass raw video directly — zero canvas overhead.
    if (!enhance && !grayscale) return video;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w === 0 || h === 0) return video;

    try {
      if (!this.preprocessCanvas) {
        this.preprocessCanvas = document.createElement('canvas');
        this.preprocessCtx = this.preprocessCanvas.getContext('2d');
      }
      if (this.preprocessCanvas.width !== w || this.preprocessCanvas.height !== h) {
        this.preprocessCanvas.width = w;
        this.preprocessCanvas.height = h;
      }
      if (!this.preprocessCtx) return video;

      // --- Adaptive brightness sampling ---
      // Every BRIGHTNESS_SAMPLE_EVERY frames, downsample the video to a tiny 32×18 probe
      // canvas and compute the mean luminance. This is cheap (~0.1ms) and avoids applying
      // any boost when the room is already well-lit.
      this.brightnessSampleFrame++;
      if (this.brightnessSampleFrame % this.BRIGHTNESS_SAMPLE_EVERY === 0) {
        if (!this.brightnessProbeCanvas) {
          this.brightnessProbeCanvas = document.createElement('canvas');
          this.brightnessProbeCanvas.width = 32;
          this.brightnessProbeCanvas.height = 18;
          this.brightnessProbeCtx = this.brightnessProbeCanvas.getContext('2d', { willReadFrequently: true });
        }
        if (this.brightnessProbeCtx) {
          this.brightnessProbeCtx.drawImage(video, 0, 0, 32, 18);
          const pixels = this.brightnessProbeCtx.getImageData(0, 0, 32, 18).data;
          let sum = 0;
          // Luma approximation: 0.299R + 0.587G + 0.114B
          for (let i = 0; i < pixels.length; i += 4) {
            sum += 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
          }
          const raw = sum / (pixels.length / 4);
          // EMA smooth (α=0.2) to avoid abrupt jumps on transient changes
          this.avgBrightness = this.avgBrightness * 0.8 + raw * 0.2;
        }
      }

      // --- Compute adaptive filter values ---
      // Well-lit: avgBrightness ≥ 100  → no boost needed  (brightness=1, contrast=1)
      // Very dark: avgBrightness ≤ 40  → full configured boost applied
      // Intermediate: linear interpolation
      // If enhance is disabled, t=0 (no brightness/contrast boost regardless of brightness)
      const maxBoostB = this.config.brightnessBoost ?? 1.35;
      const maxBoostC = this.config.contrastBoost ?? 1.20;
      const darkThreshold = 40;
      const lightThreshold = 100;
      const t = enhance
        ? Math.max(0, Math.min(1, (lightThreshold - this.avgBrightness) / (lightThreshold - darkThreshold)))
        : 0; // enhancement disabled — no brightness/contrast boost
      const brightnessFilter = 1 + (maxBoostB - 1) * t;
      const contrastFilter = 1 + (maxBoostC - 1) * t;

      // Build CSS filter string: grayscale first (before brightness) so the luma
      // computation is done on the original signal, not on an already-blown-out image.
      const filterParts: string[] = [];
      if (grayscale) filterParts.push('grayscale(1)');
      if (t >= 0.01) {
        filterParts.push(`brightness(${brightnessFilter.toFixed(3)})`);
        filterParts.push(`contrast(${contrastFilter.toFixed(3)})`);
      }
      this.preprocessCtx.filter = filterParts.length ? filterParts.join(' ') : 'none';
      this.preprocessCtx.drawImage(video, 0, 0, w, h);
      this.preprocessCtx.filter = 'none';
      return this.preprocessCanvas;
    } catch {
      return video;
    }
  }

  private assessQuality(result: FaceLandmarkerResult, faceIndex: number = 0): QualityMetrics {
    const warnings: string[] = [];
    const resolution = {
      width: this.videoElement?.videoWidth || 0,
      height: this.videoElement?.videoHeight || 0,
    };

    if (resolution.width < 640 || resolution.height < 480) {
      warnings.push('Low resolution');
    }

    let faceDetected = false;
    let faceSize = 0;
    let confidence = 0;

    if (result.faceLandmarks && result.faceLandmarks.length > 0) {
      faceDetected = true;
      const landmarks = result.faceLandmarks[Math.max(0, Math.min(faceIndex, result.faceLandmarks.length - 1))];
      const leftEye = landmarks[33];
      const rightEye = landmarks[263];
      faceSize = Math.sqrt(
        Math.pow((rightEye.x - leftEye.x) * resolution.width, 2) +
        Math.pow((rightEye.y - leftEye.y) * resolution.height, 2)
      );

      if (faceSize < 80) warnings.push('Face too small');
      else if (faceSize > 400) warnings.push('Face too close');

      const nose = landmarks[1];
      if (Math.sqrt(Math.pow(nose.x - 0.5, 2) + Math.pow(nose.y - 0.5, 2)) > 0.3) {
        warnings.push('Face not centered');
      }

      confidence = landmarks.reduce((sum: number, lm: any) => sum + (lm.visibility || 1), 0) / landmarks.length;
      if (confidence < 0.7) warnings.push('Poor lighting');
    } else {
      warnings.push('No face detected');
    }

    let quality: 'excellent' | 'good' | 'fair' | 'poor';
    if (!faceDetected) quality = 'poor';
    else if (warnings.length === 0 && confidence > 0.85) quality = 'excellent';
    else if (warnings.length <= 1 && confidence > 0.7) quality = 'good';
    else if (warnings.length <= 2) quality = 'fair';
    else quality = 'poor';

    return { resolution, faceDetected, faceSize, confidence, quality, warnings };
  }

  private selectFaceIndex(result: FaceLandmarkerResult): number {
    const faces = result.faceLandmarks ?? [];
    if (faces.length === 0) return -1;

    // ---------- Single face path ----------
    if (faces.length === 1) {
      // On mobile (selfie camera, single user), skip face-lock entirely —
      // IMAGE-mode landmark ratios diverge from the calibration descriptor
      // and the distance check will never pass (~1.5 vs 0.55 threshold).
      if (this.config.isMobileRuntime) {
        return 0;
      }
      // When face-lock is active, verify the single face matches the locked
      // identity. Without this check a different person sitting down would be
      // accepted and the EMA would slowly drift the descriptor to match them.
      if (this.faceLockDescriptor && this.config.enableSessionFaceLock !== false) {
        const d = this.computeDescriptorFromLandmarks(faces[0]);
        if (d) {
          const distance = this.computeDescriptorDistance(d, this.faceLockDescriptor);
          const threshold = this.config.faceLockDistanceThreshold ?? 0.55;
          if (distance > threshold) {
            this.faceLockConsecutiveRejects++;
            // Require several consecutive rejects before actually rejecting.
            // A single-frame spike from a yawn or expression should not
            // trigger "no face detected".
            if (this.faceLockConsecutiveRejects >= 5) {
              // After many consecutive rejects, the descriptor is likely stale
              // (e.g. hand was near face during lock, or posture changed).
              // Re-lock to the current face instead of blocking forever.
              if (this.faceLockConsecutiveRejects >= 30) {
                console.warn(`[EIE][FACE-LOCK] auto-relock after ${this.faceLockConsecutiveRejects} consecutive rejects`);
                this.faceLockDescriptor = d;
                this.faceLockConsecutiveRejects = 0;
                this.faceLockLastSeenAt = Date.now();
                return 0;
              }
              console.warn(
                `[EIE][FACE-LOCK] single face rejected: distance=${distance.toFixed(3)} > threshold=${threshold.toFixed(3)} (${this.faceLockConsecutiveRejects} consecutive)`,
              );
              return -1;
            }
            // Spike — tolerate it, still accept the face this frame.
          } else {
            this.faceLockConsecutiveRejects = 0;
          }
          // Face matches — gently refresh so descriptor tracks natural drift.
          this.faceLockDescriptor = {
            eyeNoseRatio:      this.faceLockDescriptor.eyeNoseRatio      * 0.92 + d.eyeNoseRatio      * 0.08,
            noseChinRatio:     this.faceLockDescriptor.noseChinRatio     * 0.92 + d.noseChinRatio     * 0.08,
            mouthWidthRatio:   this.faceLockDescriptor.mouthWidthRatio   * 0.92 + d.mouthWidthRatio   * 0.08,
            faceHeightRatio:   this.faceLockDescriptor.faceHeightRatio   * 0.92 + d.faceHeightRatio   * 0.08,
            eyeMouthRatio:     this.faceLockDescriptor.eyeMouthRatio     * 0.92 + d.eyeMouthRatio     * 0.08,
          };
        }
        this.faceLockLastSeenAt = Date.now();
      }
      return 0;
    }

    // ---------- No lock active: pick largest, most-centered face ----------
    if (this.config.enableSessionFaceLock === false || !this.faceLockDescriptor) {
      let bestIndex = 0;
      let bestScore = -Infinity;
      for (let i = 0; i < faces.length; i++) {
        const lm = faces[i];
        if (!lm || lm.length < 264) continue;
        const nose = lm[1];
        const left = lm[33];
        const right = lm[263];
        const scale = Math.hypot(right.x - left.x, right.y - left.y);
        const centerBias = 1 - Math.hypot(nose.x - 0.5, nose.y - 0.5);
        const score = scale * 1.4 + centerBias * 0.6;
        if (score > bestScore) {
          bestScore = score;
          bestIndex = i;
        }
      }
      return bestIndex;
    }

    // ---------- Multiple faces + lock active: match by geometry ----------
    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < faces.length; i++) {
      const d = this.computeDescriptorFromLandmarks(faces[i]);
      if (!d) continue;
      const distance = this.computeDescriptorDistance(d, this.faceLockDescriptor);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    const threshold = this.config.faceLockDistanceThreshold ?? 0.55;
    if (bestIndex >= 0 && bestDistance <= threshold) {
      const current = this.computeDescriptorFromLandmarks(faces[bestIndex]);
      if (current) {
        this.faceLockDescriptor = {
          eyeNoseRatio:      this.faceLockDescriptor.eyeNoseRatio      * 0.92 + current.eyeNoseRatio      * 0.08,
          noseChinRatio:     this.faceLockDescriptor.noseChinRatio     * 0.92 + current.noseChinRatio     * 0.08,
          mouthWidthRatio:   this.faceLockDescriptor.mouthWidthRatio   * 0.92 + current.mouthWidthRatio   * 0.08,
          faceHeightRatio:   this.faceLockDescriptor.faceHeightRatio   * 0.92 + current.faceHeightRatio   * 0.08,
          eyeMouthRatio:     this.faceLockDescriptor.eyeMouthRatio     * 0.92 + current.eyeMouthRatio     * 0.08,
        };
      }
      this.faceLockLastSeenAt = Date.now();
      return bestIndex;
    }

    return -1;
  }

  private computeDescriptorDistance(a: FaceDescriptor, b: FaceDescriptor): number {
    // Weighted Euclidean distance in 5-D ratio space.
    // Structural (bone) ratios get full weight; expression-volatile ratios
    // (mouthWidth, eyeMouth) get reduced weight so yawns / smiles don't
    // spike the distance and trip the face-lock.
    const W_STRUCT = 1.0;
    const W_EXPR   = 0.3;
    return Math.sqrt(
      W_STRUCT * (a.eyeNoseRatio    - b.eyeNoseRatio)    ** 2 +
      W_STRUCT * (a.noseChinRatio   - b.noseChinRatio)   ** 2 +
      W_EXPR   * (a.mouthWidthRatio - b.mouthWidthRatio) ** 2 +
      W_STRUCT * (a.faceHeightRatio - b.faceHeightRatio) ** 2 +
      W_EXPR   * (a.eyeMouthRatio   - b.eyeMouthRatio)   ** 2
    );
  }

  private computeDescriptorFromIndexedLandmarks(
    landmarks: Array<{ i?: number; x: number; y: number; z?: number }>,
  ): FaceDescriptor | null {
    if (!landmarks || landmarks.length < 4) return null;
    const byIdx = new Map<number, { x: number; y: number; z?: number }>();
    for (const p of landmarks) {
      if (typeof p.i === 'number') byIdx.set(p.i, p);
    }
    const left  = byIdx.get(33);
    const right = byIdx.get(263);
    const nose  = byIdx.get(1) ?? byIdx.get(4);
    const chin  = byIdx.get(152);
    const mouthL = byIdx.get(61);
    const mouthR = byIdx.get(291);
    const forehead = byIdx.get(10);
    if (!left || !right || !nose) return null;

    const interEye = Math.max(1e-6, Math.hypot(right.x - left.x, right.y - left.y));
    const eyeCX = (left.x + right.x) / 2;
    const eyeCY = (left.y + right.y) / 2;
    const eyeNoseRatio    = Math.hypot(nose.x - eyeCX, nose.y - eyeCY) / interEye;
    const noseChinRatio   = chin  ? Math.hypot(chin.x - nose.x, chin.y - nose.y) / interEye : 0;
    const mouthWidthRatio = (mouthL && mouthR) ? Math.hypot(mouthR.x - mouthL.x, mouthR.y - mouthL.y) / interEye : 0;
    const faceHeightRatio = (forehead && chin) ? Math.hypot(chin.x - forehead.x, chin.y - forehead.y) / interEye : 0;
    const mouthCX = (mouthL && mouthR) ? (mouthL.x + mouthR.x) / 2 : nose.x;
    const mouthCY = (mouthL && mouthR) ? (mouthL.y + mouthR.y) / 2 : nose.y;
    const eyeMouthRatio   = Math.hypot(mouthCX - eyeCX, mouthCY - eyeCY) / interEye;

    return { eyeNoseRatio, noseChinRatio, mouthWidthRatio, faceHeightRatio, eyeMouthRatio };
  }

  private computeDescriptorFromLandmarks(landmarks: Array<{ x: number; y: number; z?: number }>): FaceDescriptor | null {
    if (!landmarks || landmarks.length < 264) return null;
    const left     = landmarks[33];
    const right    = landmarks[263];
    const nose     = landmarks[1];
    const chin     = landmarks[152];
    const mouthL   = landmarks[61];
    const mouthR   = landmarks[291];
    const forehead = landmarks[10];
    if (!left || !right || !nose) return null;

    const interEye = Math.max(1e-6, Math.hypot(right.x - left.x, right.y - left.y));
    const eyeCX = (left.x + right.x) / 2;
    const eyeCY = (left.y + right.y) / 2;
    const eyeNoseRatio    = Math.hypot(nose.x - eyeCX, nose.y - eyeCY) / interEye;
    const noseChinRatio   = chin  ? Math.hypot(chin.x - nose.x, chin.y - nose.y) / interEye : 0;
    const mouthWidthRatio = (mouthL && mouthR) ? Math.hypot(mouthR.x - mouthL.x, mouthR.y - mouthL.y) / interEye : 0;
    const faceHeightRatio = (forehead && chin) ? Math.hypot(chin.x - forehead.x, chin.y - forehead.y) / interEye : 0;
    const mouthCX = (mouthL && mouthR) ? (mouthL.x + mouthR.x) / 2 : nose.x;
    const mouthCY = (mouthL && mouthR) ? (mouthL.y + mouthR.y) / 2 : nose.y;
    const eyeMouthRatio   = Math.hypot(mouthCX - eyeCX, mouthCY - eyeCY) / interEye;

    return { eyeNoseRatio, noseChinRatio, mouthWidthRatio, faceHeightRatio, eyeMouthRatio };
  }

  private compressBlendshapes(blendshapeData: any[]): Record<string, number> {
    const compressed: Record<string, number> = {};
    blendshapeData.forEach((b) => {
      if (b.score >= 0.01) {
        compressed[b.categoryName] = Math.round(b.score * 100) / 100;
      }
    });
    return compressed;
  }
}
